import {isNil} from 'ramda';
import {isNilOrEmpty, renameKeysWith} from 'ramda-adjunct';

import {useListMyCredentialsQuery, useListUserCredentialsQuery} from '@dms/api/core';
import {EmployeeAuthorizationIntegrationBody, useGetCurrentUserInfoQuery} from '@dms/api/customer';

type UseListCredentialsProps = {
  userId: string;
  integrations: EmployeeAuthorizationIntegrationBody[];
};

export const useListCredentials = (props: UseListCredentialsProps) => {
  const {data: currentUserInfo} = useGetCurrentUserInfoQuery();
  const isCurrentUser = currentUserInfo?.id === props.userId;

  const {
    data: externalCredentialsData,
    isLoading: isLoadingExternalCredentials,
    refetch: refetchExternalCredentials,
  } = useListUserCredentialsQuery(
    {
      userId: props.userId ?? '',
      params: renameKeysWith(
        (index) => `authorizationIds[${index}]`,
        props.integrations.map((integration) => integration.authorizationProfileId)
      ),
    },
    {
      skip: isNil(props.userId) || isNilOrEmpty(props.integrations) || isCurrentUser,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: myExternalCredentialsData,
    isLoading: isLoadingMyExternalCredentials,
    refetch: refetchMyExternalCredentials,
  } = useListMyCredentialsQuery(
    {
      params: renameKeysWith(
        (index) => `authorizationIds[${index}]`,
        props.integrations.map((integration) => integration.authorizationProfileId)
      ),
    },
    {skip: isNilOrEmpty(props.integrations) || !isCurrentUser}
  );

  const credentials = isCurrentUser ? myExternalCredentialsData : externalCredentialsData;
  const isLoading = isCurrentUser ? isLoadingMyExternalCredentials : isLoadingExternalCredentials;
  const refetch = isCurrentUser ? refetchMyExternalCredentials : refetchExternalCredentials;

  return {
    credentials,
    isLoading,
    refetch,
  };
};
